import { useEffect, useState } from 'react';
import { useCookies } from 'react-cookie';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import classNames from 'classnames';

import Modal from 'components/Modal';
import WelcomePopup from 'components/WelcomePopup';
import { EDUCATIONAL_PORTAL_BASE_URL } from 'constants/locations';
import useDevice from 'hooks/useDevice';
import { getNewCustomerCookieName } from 'redux/modules/appConfigs/selectors';
import { getEnvironmentRootPath } from 'utils/navigation';

import styles from './styles.module.scss';

const WelcomeNewUserComponent = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { isMobile } = useDevice();

  const newCustomerCookieName = useSelector(getNewCustomerCookieName);

  const [, setCookie] = useCookies([newCustomerCookieName]);

  const [closeWelcomePopup, setCloseWelcomePopup] = useState<string>('');
  const [isWelcomeOpen, setIsWelcomeOpen] = useState<boolean>(true);

  const clearNewCustomerCookie = () => {
    setCookie(newCustomerCookieName, null, {
      path: getEnvironmentRootPath(),
      expires: new Date(0)
    });
  };

  useEffect(() => {
    if (closeWelcomePopup) {
      const timer = setTimeout(() => {
        setIsWelcomeOpen(false);
        setCloseWelcomePopup('');
        clearNewCustomerCookie();

        if (closeWelcomePopup === 'edu') {
          navigate(EDUCATIONAL_PORTAL_BASE_URL);
        }
      }, 300);

      return () => clearTimeout(timer);
    }
  }, [closeWelcomePopup]);

  const handleCloseWelcomePopup = () => {
    setCloseWelcomePopup('close');
  };

  const handleLearnMore = () => {
    setCloseWelcomePopup('edu');
  };

  return (
    <>
      <Modal
        isDisableScroll
        open={isWelcomeOpen}
        onClose={handleCloseWelcomePopup}
        title={t('dialog.welcome.title')}
        customClassNames={{
          body: styles.welcomeModalBody,
          dialog: classNames(styles.welcomeModal, {
            [styles.welcomeModalFadein]: !closeWelcomePopup,
            [styles.welcomeModalFadeout]: closeWelcomePopup
          }),
          content: classNames(styles.welcomeModalContent, {
            [styles.welcomeModalContent__mobile]: isMobile
          }),
          header: classNames(styles.welcomeModalHeader, {
            [styles.welcomeModalHeader__mobile]: isMobile
          }),
          backdrop: classNames({ [styles.hideModalBackdrop]: closeWelcomePopup }),
          headerContent: styles.welcomeModalHeaderContent
        }}
      >
        <WelcomePopup onLearnMore={handleLearnMore} />
      </Modal>
      {isWelcomeOpen && (
        <div
          onClick={handleCloseWelcomePopup}
          className={classNames(styles.backdrop, {
            [styles.overlapHowTo]: !isWelcomeOpen && !isMobile,
            [styles.overlapMobileMenu]: !isWelcomeOpen && isMobile,
            [styles.backdropFadein]: isWelcomeOpen
          })}
        />
      )}
    </>
  );
};

export default WelcomeNewUserComponent;
